import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { useBrand } from '@hcsc-fiber/react';
import API from '../API';
import { getDateInUIFormat } from '../helpers/dateHelper';
import {
  areValuesDefined,
  getSessionData,
  SESSION_DATA,
  updateSessionData,
} from '../helpers/storeHelper';
import {
  getOriginType,
  getStateCodeFromParsedUrl,
  getStateCodeSpecificUrlUsingProcessEnv,
} from '../helpers/navigationHelper';
import { apiStatusReducer, pkceReducer, pkceReducerInit } from './reducer';
import { setPageTitle } from '../helpers/htmlHelper';
import { useHistory } from 'react-router-dom';
import { routeMap, ROUTES } from '../constants/routes';
import { BRAND_CODES } from '../brand/constants';
import {
  getBillsResponseInfo,
  getDisplayAutopayDraftDate,
  getExchangeIndicatorFromMemberBills,
  getGovtBillResponseInfo,
  getProductDetail,
  getSelectedCimDetails,
  getSortedBillsAscByDueDate,
  updateAmountAndMinAmountForNoBills,
} from '../helpers/billHelper';
import {
  IAPIStatus,
  IAppContextValue,
  IAutopayAlertDetails,
  IBillsList,
  IGetWalletResponse,
  IImmediatePaymentDetails,
  IOneTimeSchedulePayment,
  IScheduledTransaction,
  ILoaderDetails,
  IMemberDetails,
  IPaymentDetails,
  IScheduledMaintenanceDetails,
  IScheduleOneTimePaymentDetails,
  ITokenDetails,
  IWalletsData,
  PAYMENT_API_TYPE,
  IWalletAlertDetails,
  IGlobalTechnicalDifficulty,
  IOneTimePaymentErrors,
  IAutoPayPaymentMethods,
  IGetAutoPaymentDetails,
  PAYMENT_METHOD_TYPES,
  IPKCEStatus,
  IAuthenticationError,
  IUserDetails,
  IFetchWallet,
  IBinderPayNowLoginDetails,
  IBinderSchedulePayment,
  IBinderImmediatePayment,
  IBinderPaymentMethod,
  IFiservResponse,
  IAutopayBinderPaymentMethod,
  IApplicantBinderDetailsResponse,
  IBinderBillDetails,
  IBinderPaymentDetails,
  IScheduledEmailErrors,
  SCHEDULED_EMAIL_ERROR,
  IActiveUserEmailResponse,
  IHPSEnrolment,
  IBillResponseAndSessionData,
  IOnSuccessPaymentBills,
  IRecentPaymentProps,
  IGovernmentProductDetails,
  IMemberBillingInfoRequest,
  IMenuItemsConfiguration,
  IEmailConfirmationStatus,
  IFundingAcountDetailsForBroker,
} from './interface';
import { API_STATUS_TYPES } from './constants';
import { addTypeToWalletsList } from '../helpers/walletsHelper';
import useUserType from '../customHooks/useUserType';
import useAPIHeaders from './../customHooks/useAPIHeaders';
import { AccessTokenDetails } from '../API/models/PKCEAPIModels';
import {
  CUSTOMER_SERVICE_NUMBER_PLACEHOLDER_CAMELCASE,
  HELPDESK_NUMBER_PLACEHOLDER,
  MEDICARE_LINK,
  MEDICARE_LINK_TEXT,
  ORIGIN_TYPES,
  PLAN_TYPES,
  SCHEDULE_MAINTENANCE_APPLICATION_TYPES,
  USER_TYPES,
} from '../constants/common';
import { IActiveMemberEmailHeaders } from '../API/modules/member';
import { MEMBER_STATE_CODE } from '../constants/memberConstants';
import {
  PAYMENT_STATUS,
  PAYMENT_TYPES,
} from '../constants/recentPaymentConstants';
import useContactDetails from '../customHooks/useContactDetails';
import { useAEMContext } from './aemStore';
import { COMMON_COMPONENTS } from '../AEM/constants';
import { fetchFromContentService } from '../AEM/utils';
import { getGovtUserType } from '../helpers/memberHelper';

const initialPaymentDetails: IPaymentDetails = {
  paymentMethod: undefined, // TODO earlier it was defaultPaymentMethod
  addToWallet: false,
  email: undefined,
};

export type ISetApiStatus = (_statusType: string, _Action: string) => void;

export type ISetAuthenticationError = (
  _err: IAuthenticationError,
  _callBack?: () => void,
) => void;

export type ISetPKCEStatus = (_statusType: string, _action: string) => void;

export const intialiseMemberDetails = (): IMemberDetails => {
  const { USER_TYPE: userTypeStorage } = getSessionData();
  const resetMemberData: IMemberDetails = initialMemberDetails;
  if (userTypeStorage) {
    switch (Number(userTypeStorage)) {
      case USER_TYPES.NMA_GOVT_USER:
        resetMemberData.userType = USER_TYPES.NMA_GOVT_USER;
        break;
      case USER_TYPES.NMA_IFM_USER:
        resetMemberData.userType = USER_TYPES.NMA_IFM_USER;
        break;
      case USER_TYPES.ONE_TIME_PAYMENT_ACTIVE_MEMBER:
        resetMemberData.userType = USER_TYPES.ONE_TIME_PAYMENT_ACTIVE_MEMBER;
        break;
      case USER_TYPES.CA_TERMED_MEMBER:
        resetMemberData.userType = USER_TYPES.CA_TERMED_MEMBER;
        break;
      case USER_TYPES.CUSTOMER_ADVOCATE:
        resetMemberData.userType = USER_TYPES.CUSTOMER_ADVOCATE;
        break;
      case USER_TYPES.BAM_PUBLICSITE_ACTIVE_MEMBER:
        resetMemberData.userType = USER_TYPES.BAM_PUBLICSITE_ACTIVE_MEMBER;
        break;
      case USER_TYPES.ONE_TIME_GOVERNMENT_ACTIVE_MEMBER:
        resetMemberData.userType = USER_TYPES.ONE_TIME_GOVERNMENT_ACTIVE_MEMBER;
        break;
      case USER_TYPES.BAM_GOVERNMENT_ACTIVE_MEMBER:
        resetMemberData.userType = USER_TYPES.BAM_GOVERNMENT_ACTIVE_MEMBER;
        break;
      case USER_TYPES.CA_GOVT_USER:
        resetMemberData.userType = USER_TYPES.CA_GOVT_USER;
        break;
      case USER_TYPES.BROKER:
        resetMemberData.userType = USER_TYPES.BROKER;
        break;
      case USER_TYPES.GUEST:
      default:
        resetMemberData.userType = USER_TYPES.GUEST;
        break;
    }
  } else {
    resetMemberData.userType = USER_TYPES.GUEST; // reset to guest if user type is not present in session storage
  }

  return resetMemberData;
};

export const initialAPIStatus: IAPIStatus = {
  [API_STATUS_TYPES.LOADER_SCREEN_COUNTER]: 0,
  [API_STATUS_TYPES.SHOW_ERROR_SCREEN]: false,
};

export const initialPKCEStatus: IPKCEStatus = {
  verifier: '',
  challenge: '',
};

// const initialPaymentDetails: IPaymentDetails = {
//   paymentMethod: defaultPaymentMethod,
// };

const initialMemberDetails: IMemberDetails = {
  userType: USER_TYPES.GUEST, // default user type guest, on authentication usertype will be active member
  conversionTypeDetails: {
    conversionType: undefined,
    lockdownPeriod: false,
    freezePeriod: false,
    forcedConvNonAutopay: undefined,
    showAPForcedConverted: false, // here AP is Autopay
  },
};
const initialLoaderDetails: ILoaderDetails = {
  isVisible: false,
};

const initialBillDetails: IBillsList = {
  bills: undefined,
  hasMultipleBills: false,
  noBillIndicator: false,
  hasMultipleBillsWithValue: false,
  displayAutopayDraftDate: undefined,
};

const initialStateCode = '';
const initialLanguage = 'EN';

export const initialAuthDetails: ITokenDetails = {
  token: '',
  session: '',
  messageId: '',
  timeStamp: '',
  sessionValidFlag: false,
};

const initialAccessToken: AccessTokenDetails = {
  access_token: '',
  refresh_token: '',
  expires_in: '',
  id_token: '',
};

const initialUserDetails: IUserDetails = {
  emailId: '',
  userName: '',
  origin: ORIGIN_TYPES.GUEST, // default user type guest, on authentication usertype will be active member
};

export const initialImmediatePaymentDetails: IImmediatePaymentDetails = {
  confirmationCode: '',
  paymentIndicator: false,
  paymentDateTimeStamp: '',
};

export const initialListScheduledOneTimePayments: IOneTimeSchedulePayment = {
  isLoaded: false,
  scheduledPayments: [],
  count: 0,
};

export const initialSelectedScheduledTransaction: IScheduledTransaction = {
  addDate: '',
  billingAccount: '',
  billingSubAccount: '',
  emailAddress: '',
  feeAmount: 0,
  fundingAccount: {
    fundingAccountLastFourDigit: '',
    fundingAccountToken: '',
    fundingCategory: '',
    nickname: '',
    bankAccountType: '',
  },
  lastUpdatedDate: '',
  paymentAmount: 0,
  paymentDate: '',
  referenceCode: '',
  referenceId: '',
  status: '',
  activeAutoPay: false,
};

const initialGetAutoPaymentDetails: IGetAutoPaymentDetails = {
  autoPayment: null,
  paymentDateStr: '',
  isNextPaymentDateWithinXDays: false,
  isActive: false,
  billedPremium: '',
  latePaymentAmount: '',
  emailAddress: '',
  allowToDisable: true,
  allowToUpdate: true,
};

const initialScheduleOneTimePaymentDetails: IScheduleOneTimePaymentDetails = {
  addedToWallet: false,
};

export const initialScheduledMaintenanceDetails: IScheduledMaintenanceDetails =
  {
    active: false,
    message: '',
    endDateTime: '',
    isLoaded: false,
  };
const initialApiPaymentType = PAYMENT_API_TYPE.NONE;
const initialBinderApiPaymentType = PAYMENT_API_TYPE.NONE;
const initialMemberEmail: IActiveUserEmailResponse = {
  email: undefined,
};

const initialWalletsData: IWalletsData = {};
export const initialFiservResponse: IFiservResponse = {
  fundingAccountValidationResult: {},
};
const initialWalletAlertData: IWalletAlertDetails = {};
const initialAutoPayPaymentMethodsData: IAutoPayPaymentMethods = {
  isLoaded: false,
  wallets: [],
};

export const initialBinderPaymentMethod: IBinderPaymentMethod = {
  fiservResponse: initialFiservResponse,
  savePaymentMethod: false,
  selectedPaymentMethodType: '',
  paymentDate: '',
  selectedPaymentAmountType: '',
  paymentAmount: 0,
  selectedPaymentMethodName: '',
  nickname: '',
};

export const initialBinderPaymentDetails: IBinderPaymentDetails = {
  selectedBillCimNumber: '',
  selectedCaseNumber: '',
  exchangeIndicator: '',
  remainingBinderAmount: 0,
  billNames: '',
  dueDate: '',
  upcomingPaymentIndicator: true,
  minimumRequiredPremium: 0,
  applicationId: '',
  mppId: 0,
};

export const initialBinderAutoPaymentMethod: IAutopayBinderPaymentMethod = {
  fiservResponse: initialFiservResponse,
  savePaymentMethod: false,
  setAutopay: false,
  selectedPaymentMethodType: '',
  nickname: '',
  accountHolderEmailAddress: '',
};

export const initialIsBrokerUserAutopayFailed = false;

const initialAutopayAlertData: IAutopayAlertDetails = {};
export const initialGlobalTechnicalDifficulty: IGlobalTechnicalDifficulty = {
  errorCode: '',
};

export const initialOneTimePaymentErrors: IOneTimePaymentErrors = {
  alertMessage: '',
  alertCaMessage: '',
  alertType: undefined,
  showAlert: false,
  showBillingVelocityReachedError: false,
  showUnabletoProcessError: false,
  showEmailAddressNotPresentError: false,
  showEmailAddressFailedValidationError: false,
  keepHtmL: false,
};

export const initialScheduledEmailErrors: IScheduledEmailErrors = {
  alertMessage: SCHEDULED_EMAIL_ERROR.NONE,
  alertCaMessage: '',
  alertType: undefined,
  showAlert: false,
  error: false,
};

export const initialAuthenticationErrors: IAuthenticationError = {
  showError: false,
  authenticationError: false,
  errAPIResponse: {},
};

export const initialApplicantBinderDetails: IApplicantBinderDetailsResponse = {
  applicantValidationList: [{ mppId: 0, applicationId: '', state: '' }],
  email: '',
  effectiveDate: '',
};

export const initialSchedulePaymentResponse: IBinderSchedulePayment = {
  errorMessage: null,
  response: {
    referenceCode: '',
    referenceId: '',
    paymentAmount: 0,
  },
};

export const initialBinderImmediatePayment: IBinderImmediatePayment = {
  errorMessage: null,
  response: {
    confirmationCode: '',
    fundingMethod: '',
    amountProcessed: '',
  },
};

export const initialHPSEnrolment: IHPSEnrolment = {
  showAutopayModal: false,
  isNewlyConverted: false,
  isHPSAutopayEnabled: false,
  openAutopayDrawer: false,
};

export const initialBinderPayNowLoginDetails: IBinderPayNowLoginDetails = {
  applicationId: '',
  dateOfBirth: '',
  exchangeIndicator: '',
  memberStateCode: '',
  memberZipCode: undefined,
  paymentAmount: 0,
  sourceSystem: '',
  email: '',
  monthlyBill: 0,
  redirectURI: '',
  mppId: undefined,
  binderPaymentStatus: undefined,
};

export const initialBinderBillDetails: IBinderBillDetails = {
  binderBills: [{ applicationId: '', mppId: 0 }],
  hasMultipleBills: false,
};

export const initialSelectedRecentPayment: IRecentPaymentProps = {
  paymentAmount: 0,
  confirmationNumber: '',
  paymentDate: '',
  paymentMethod: '',
  paymentType: PAYMENT_TYPES.IMMEDIATE,
  paymentEvents: [
    {
      status: PAYMENT_STATUS.SCHEDULED,
      appliedDate: '',
    },
  ],
  paymentTrackingId: 0,
};

export const initialMenuItemsConfiguration: IMenuItemsConfiguration = {
  paymentHistoryPage: false,
  billingHistoryPage: false,
  myWalletsPage: false,
  recentPaymentsSection: false,
  autoBillPaySection: false,
  autoBillPayManageLink: false,
  isLoaded: false,
  schedulePaymentPage: false,
};

export const initialEmailConfirmationStatus: IEmailConfirmationStatus = {
  mailSent: false,
  message: '',
  isLoaded: false,
};

export const initialFundingAccountDetailsForBroker: IFundingAcountDetailsForBroker =
  {
    lastFourDigits: '',
    fname: '',
  };

export const initialBrokerAutopayPaymentMethod = '';

export const initialBrokerFundingAccountLastFourDigits = '';

export const AppContext = createContext({
  paymentDetails: initialPaymentDetails,
  memberDetails: intialiseMemberDetails(),
  loaderDetails: initialLoaderDetails,
  billDetails: initialBillDetails,
  authDetails: initialAuthDetails,
  listScheduledOneTimePayments: initialListScheduledOneTimePayments,
  selectedScheduledTransaction: initialSelectedScheduledTransaction,
  immediatePayDetails: initialImmediatePaymentDetails,
  scheduleOneTimePaymentDetails: initialScheduleOneTimePaymentDetails,
  scheduledMaintenanceDetails: initialScheduledMaintenanceDetails,
  autoPaymentDetails: initialGetAutoPaymentDetails,
  oneTimePaymentErrors: initialOneTimePaymentErrors,
  scheduledEmailErrors: initialScheduledEmailErrors,
  paymentApiType: initialApiPaymentType,
  memberEmail: initialMemberEmail,
  binderPaymentApiType: initialBinderApiPaymentType,
  autoPayPaymentMethods: initialAutoPayPaymentMethodsData,
  binderPaymentMethod: initialBinderPaymentMethod,
  binderPaymentDetails: initialBinderPaymentDetails,
  autopayBinderPaymentMethod: initialBinderAutoPaymentMethod,
  //this context maybe removed later
  binderPayNowLoginDetails: initialBinderPayNowLoginDetails,
  binderBillDetails: initialBinderBillDetails,
  applicantBinderDetails: initialApplicantBinderDetails,
  binderSchedulePaymentResponse: initialSchedulePaymentResponse,
  binderImmediatePaymentResponse: initialBinderImmediatePayment,
  showConversionModal: false,
  showOldConversionModal: false,
  /*eslint-disable*/
  displayAutopayDetails: true,
  binderPayNowTransactionId: '',
  readOnlyView: false,
  autopayEnrolment: initialHPSEnrolment,
  limitFunctionality: false,
  showAutopaySetupModal: false,
  showEconsentModal: false,
  isLoadingAfterLogin: false,
  setMemberEmail: (_memberEmail: IActiveUserEmailResponse) => {},
  setBinderPayNowLoginDetails: (
    _binderPayNowLoginDetails: IBinderPayNowLoginDetails,
  ) => {},
  setBinderSchedulePaymentResponse: (
    _binderOneTimeSchedulePayment: IBinderSchedulePayment,
  ) => {},
  setBinderImmediatePaymentResponse: (
    _binderImmediatePaymentResponse: IBinderImmediatePayment,
  ) => {},
  setBinderBillDetails: (_binderBillDetails: IBinderBillDetails) => {},
  setApplicantBinderDetails: (
    _applicantBinderDetails: IApplicantBinderDetailsResponse,
  ) => {},
  setDisplayAutopayDetails: (_displayAutopayDetails: boolean) => {},
  setBinderPayNowTransactionId: (_binderPayNowTransactionId: string) => {},
  setReadOnlyView: (_readOnlyView: boolean) => {},
  setAutopayEnrolment: (_autopayEnrolment: IHPSEnrolment) => {},
  setLimitFunctionality: (limitFunctionality: boolean) => {},
  setPaymentDetails: (_paymentDetails: IPaymentDetails) => {},
  setPaymentApiType: (_paymentApiType: PAYMENT_API_TYPE) => {},
  setBinderPaymentApiType: (_binderPaymentApiType: PAYMENT_API_TYPE) => {},
  setMemberDetails: (_memberDetails: IMemberDetails) => {},
  memberAuthenticationError: initialAuthenticationErrors,
  userDetails: initialUserDetails,
  setUserDetails: (_userDetails: IUserDetails) => {},
  accessTokenData: initialAccessToken,
  setAccessTokenData: (_accTokenDetails: AccessTokenDetails) => {},
  showLoader: (_message?: string) => {},
  hideLoader: () => {},
  setBillDetails: (_billsList: IBillsList) => {},
  resetContext: () => {},
  hydrateStore: (_billsResponseData: IBillResponseAndSessionData) => {},
  isHydrating: false,
  retrieveStateCodeFromUrl: () => {},
  stateCode: initialStateCode,
  setAuthDetails: (_authDetails: ITokenDetails) => {},
  setImmediatePayDetails: (_payDetails: IImmediatePaymentDetails) => {},
  setScheduleOneTimePaymentDetails: (
    _scheduleOneTimePaymentDetails: IScheduleOneTimePaymentDetails,
  ) => {},
  setListScheduledOneTimePayments: (
    _listScheduledOneTimePayments: IOneTimeSchedulePayment,
  ) => {},
  setSelectedScheduledTransaction: (
    _selectedScheduledTransaction: IScheduledTransaction,
  ) => {},
  setAutoPaymentDetails: (_autoPaymentDetails: IGetAutoPaymentDetails) => {},
  setAPIStatus: (_statusType: string, _Action: string) => {},
  APIStatus: initialAPIStatus,
  PKCEState: initialPKCEStatus,
  setPKCEStatus: (_statusType: string, _Action: string) => {},
  setPaymentMethodOptions: (__paymentMethodOptions: Array<string>) => {},
  setAuthenticationError: (
    _err: IAuthenticationError,
    _callBack?: () => void,
  ) => {},
  paymentMethodOptions: [''],
  setScheduledMaintenanceDetails: (
    _scheduledMaintenanceDetails: IScheduledMaintenanceDetails,
  ) => {},
  language: initialLanguage,
  setOneTimePaymentErrors: (_oneTimePaymentErrors: IOneTimePaymentErrors) => {},
  setScheduledEmailErrors: (_scheduledEmailErrors: IScheduledEmailErrors) => {},
  walletsData: initialWalletsData,
  setWalletsData: (_walletsData: IWalletsData) => {},
  setWalletAlertDetails: (
    _walletConfirmationDetails: IWalletAlertDetails,
  ) => {},
  walletAlertDetails: initialWalletAlertData,
  isBrokerUserWithAutopayFailed: initialIsBrokerUserAutopayFailed,
  setIsBrokerUserWithAutopayFailed: (
    _isBrokerUserWithAutopayFailed: boolean,
  ) => {},
  setGlobalTechnicalDifficulty: (
    _globalTechnicalDifficulty: IGlobalTechnicalDifficulty,
  ) => {},
  globalTechnicalDifficulty: initialGlobalTechnicalDifficulty,
  fetchWallets: (_fetchWallet: IFetchWallet) => {},
  fetchBinderWallets: (_fetchBinderWallet: IFetchWallet) => {},
  resetWalletAlertDetails: () => {},
  setBinderPaymentMethod: (_binderPaymentMethod: IBinderPaymentMethod) => {},
  setBinderPaymentDetails: (_binderPaymentDetails: IBinderPaymentDetails) => {},
  setAutopayBinderPaymentMethod: (
    _autopayBinderPaymentMethod: IAutopayBinderPaymentMethod,
  ) => {},
  setAutoPayPaymentMethods: (
    _autoPayPaymentMethods: IAutoPayPaymentMethods,
  ) => {},
  setAutopayAlertDetails: (
    _AutopayConfirmationDetails: IAutopayAlertDetails,
  ) => {},
  autopayAlertDetails: initialAutopayAlertData,
  resetAutopayAlertDetails: () => {},
  setSelectedTabUrl: (__selectedTabUrl: string) => {},
  selectedTabUrl: '',
  setShowConversionModal: (_showConversionModal: boolean) => {},
  setOldConversionModal: (_showOldConversionModal: boolean) => {},
  //For setCaId
  setCaId: (_caId: string) => {},
  caId: '',
  selectedRecentPayment: initialSelectedRecentPayment,
  setSelectedRecentPayment: (_selectedRecentPayment: IRecentPaymentProps) => {},
  setShowAutopaySetupModal: (_showAutopaySetupModal: boolean) => {},
  setShowEconsentModal: (_showEconsentModal: boolean) => {},
  menuItemsConfiguration: initialMenuItemsConfiguration,
  setMenuItemsConfiguration: (
    _menuItemsConfiguration: IMenuItemsConfiguration,
  ) => {},
  setIsLoadingAfterLogin: (_isLoadingAfterLogin: boolean) => {},
  planSelected: '',
  setPlanSelected: (_planSelected: PLAN_TYPES) => {},
  showChangeViewPlanOptions: false,
  setChangeViewPlanOptions: (_showChangeViewPlanOptions: boolean) => {},
  hasGovtScheduledPayments: false,
  setHasGovtScheduledPayments: (_hasGovtScheduledPayments: boolean) => {},
  schedulePaymentCount: 0,
  setSchedulePaymentCount: (_schedulePaymentCount: number) => {},
  isMedsupAutopayLoaded: false,
  setIsMedsupAutopayLoaded: (_isMedsupAutopayLoaded: boolean) => {},
  brokerAutopayPaymentMethod: initialBrokerAutopayPaymentMethod,
  setBrokerAutopayPaymentMethod: (_brokerAutopayPaymentMethod: string) => {},
  emailConfirmationStatus: initialEmailConfirmationStatus,
  setEmailConfirmationStatus: (
    _emailConfirmationStatus: IEmailConfirmationStatus,
  ) => {},
  fundingAccountDetailsForBroker: initialFundingAccountDetailsForBroker,
  setFundingAccountDetailsForBroker: (
    _fundingAccountDetailsForBroker: IFundingAcountDetailsForBroker,
  ) => {},
  referenceCodeForBroker: '',
  setReferenceCodeForBroker: (_setReferenceCodeForBroker: string) => {},
  formatedDate: '',
  setFormatedDate: (_formatedDate: string) => {},
  errorCodeForValidateBroker: '',
  setErrorCodeForValidateBroker: (_errorCodeForValidateBroker: string) => {},
  /*eslint-enable */
});

export function WithAppContext({
  children,
}: {
  children: JSX.Element;
}): JSX.Element {
  const history = useHistory();
  const { brand } = useBrand();
  const brandCode =
    brand === BRAND_CODES.HEALTHSPRING
      ? BRAND_CODES.HEALTHSPRING
      : BRAND_CODES.BCBS;
  const { getHoursOfOperationMessageForGov } = useContactDetails();
  const { AEMCommonContent } = useAEMContext();
  const {
    hoursOfOperation: { isLoaded },
  } = AEMCommonContent;
  const {
    getStateCodeCustomerServiceNumberByUserType,
    getCustomerServiceNumberForGovernment,
  } = useUserType();
  const language = initialLanguage;
  const [paymentDetails, setPaymentDetails] = useState<IPaymentDetails>(
    initialPaymentDetails,
  );
  const [brokerAutopayPaymentMethod, setBrokerAutopayPaymentMethod] =
    useState<string>(initialBrokerAutopayPaymentMethod);
  const [memberDetails, setMemberDetails] = useState<IMemberDetails>(
    intialiseMemberDetails(),
  );
  const [memberAuthenticationError, setMemberAuthenticationError] = useState(
    initialAuthenticationErrors,
  );
  const [userDetails, setUserDetails] =
    useState<IUserDetails>(initialUserDetails);
  const [accessTokenData, setAccessTokenData] =
    useState<AccessTokenDetails>(initialAccessToken);
  const [loaderDetails, setLoaderDetails] =
    useState<ILoaderDetails>(initialLoaderDetails);
  const [billDetails, setBillDetails] =
    useState<IBillsList>(initialBillDetails);
  const [isHydrating, setIsHydrating] = useState(true);
  const [stateCode, setStateCode] = useState<string>(initialStateCode);
  const [authDetails, setAuthDetails] =
    useState<ITokenDetails>(initialAuthDetails);
  const [immediatePayDetails, setImmediatePayDetails] =
    useState<IImmediatePaymentDetails>(initialImmediatePaymentDetails);
  const [scheduleOneTimePaymentDetails, setScheduleOneTimePaymentDetails] =
    useState<IScheduleOneTimePaymentDetails>(
      initialScheduleOneTimePaymentDetails,
    );
  const [listScheduledOneTimePayments, setListScheduledOneTimePayments] =
    useState<IOneTimeSchedulePayment>(initialListScheduledOneTimePayments);
  const [selectedScheduledTransaction, setSelectedScheduledTransaction] =
    useState<IScheduledTransaction>(initialSelectedScheduledTransaction);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<
    Array<string>
  >([]);
  const [walletsData, setWalletsData] = useState<IWalletsData>({});
  const [walletAlertDetails, setWalletAlertDetails] =
    useState<IWalletAlertDetails>({});
  const [isBrokerUserWithAutopayFailed, setIsBrokerUserWithAutopayFailed] =
    useState<boolean>(false);
  const [globalTechnicalDifficulty, setGlobalTechnicalDifficulty] =
    useState<IGlobalTechnicalDifficulty>(initialGlobalTechnicalDifficulty);
  const [scheduledMaintenanceDetails, setScheduledMaintenanceDetails] =
    useState<IScheduledMaintenanceDetails>(initialScheduledMaintenanceDetails);
  const [oneTimePaymentErrors, setOneTimePaymentErrors] =
    useState<IOneTimePaymentErrors>(initialOneTimePaymentErrors);
  const [scheduledEmailErrors, setScheduledEmailErrors] =
    useState<IScheduledEmailErrors>(initialScheduledEmailErrors);
  const [autoPaymentDetails, setAutoPaymentDetails] =
    useState<IGetAutoPaymentDetails>(initialGetAutoPaymentDetails);
  const [paymentApiType, setPaymentApiType] = useState<PAYMENT_API_TYPE>(
    initialApiPaymentType,
  );
  const [memberEmail, setMemberEmail] =
    useState<IActiveUserEmailResponse>(initialMemberEmail);
  const [autopayEnrolment, setAutopayEnrolment] =
    useState<IHPSEnrolment>(initialHPSEnrolment);
  const [binderPaymentApiType, setBinderPaymentApiType] =
    useState<PAYMENT_API_TYPE>(initialBinderApiPaymentType);
  const [autoPayPaymentMethods, setAutoPayPaymentMethods] =
    useState<IAutoPayPaymentMethods>(initialAutoPayPaymentMethodsData);
  const [binderPaymentMethod, setBinderPaymentMethod] =
    useState<IBinderPaymentMethod>(initialBinderPaymentMethod);
  const [binderPaymentDetails, setBinderPaymentDetails] =
    useState<IBinderPaymentDetails>(initialBinderPaymentDetails);
  const [autopayBinderPaymentMethod, setAutopayBinderPaymentMethod] =
    useState<IAutopayBinderPaymentMethod>(initialBinderAutoPaymentMethod);
  const [autopayAlertDetails, setAutopayAlertDetails] =
    useState<IAutopayAlertDetails>(initialAutopayAlertData);
  const [binderPayNowLoginDetails, setBinderPayNowLoginDetails] =
    useState<IBinderPayNowLoginDetails>(initialBinderPayNowLoginDetails);
  const [applicantBinderDetails, setApplicantBinderDetails] =
    useState<IApplicantBinderDetailsResponse>(initialApplicantBinderDetails);
  const [binderSchedulePaymentResponse, setBinderSchedulePaymentResponse] =
    useState<IBinderSchedulePayment>(initialSchedulePaymentResponse);
  const [binderImmediatePaymentResponse, setBinderImmediatePaymentResponse] =
    useState<IBinderImmediatePayment>(initialBinderImmediatePayment);
  const [binderBillDetails, setBinderBillDetails] =
    useState<IBinderBillDetails>(initialBinderBillDetails);
  //should be removed later when BAM/binder flow comes in
  const [selectedTabUrl, setSelectedTabUrl] = useState<string>('');
  //get ca details
  const [caId, setCaId] = useState<string>('');
  const [displayAutopayDetails, setDisplayAutopayDetails] =
    useState<boolean>(true);
  const [binderPayNowTransactionId, setBinderPayNowTransactionId] =
    useState<string>('');
  const [readOnlyView, setReadOnlyView] = useState<boolean>(false);
  const [limitFunctionality, setLimitFunctionality] = useState<boolean>(false);
  const showLoader = (message?: string) => {
    setLoaderDetails({ isVisible: true, message });
  };
  const hideLoader = () => {
    setLoaderDetails({ isVisible: false });
  };
  const [showConversionModal, setShowConversionModal] =
    useState<boolean>(false);
  const [showAutopaySetupModal, setShowAutopaySetupModal] =
    useState<boolean>(false);
  const [showEconsentModal, setShowEconsentModal] = useState<boolean>(false);
  const [emailConfirmationStatus, setEmailConfirmationStatus] =
    useState<IEmailConfirmationStatus>(initialEmailConfirmationStatus);
  const [isLoadingAfterLogin, setIsLoadingAfterLogin] =
    useState<boolean>(false);
  const [hasGovtScheduledPayments, setHasGovtScheduledPayments] =
    useState<boolean>(false);
  const [schedulePaymentCount, setSchedulePaymentCount] = useState<number>(0);
  const [isMedsupAutopayLoaded, setIsMedsupAutopayLoaded] =
    useState<boolean>(false);
  const [showOldConversionModal, setOldConversionModal] =
    useState<boolean>(false);
  const [selectedRecentPayment, setSelectedRecentPayment] =
    useState<IRecentPaymentProps>(initialSelectedRecentPayment);
  const [menuItemsConfiguration, setMenuItemsConfiguration] =
    useState<IMenuItemsConfiguration>(initialMenuItemsConfiguration);
  const [planSelected, setPlanSelected] = useState<PLAN_TYPES>(PLAN_TYPES.NA);
  const [showChangeViewPlanOptions, setChangeViewPlanOptions] =
    useState<boolean>(false);
  const [fundingAccountDetailsForBroker, setFundingAccountDetailsForBroker] =
    useState<IFundingAcountDetailsForBroker>(
      initialFundingAccountDetailsForBroker,
    );
  const [referenceCodeForBroker, setReferenceCodeForBroker] =
    useState<string>('');
  const [formatedDate, setFormatedDate] = useState<string>('');
  const [errorCodeForValidateBroker, setErrorCodeForValidateBroker] =
    useState<string>('');

  const resetContext = () => {
    setPaymentDetails(initialPaymentDetails);
    setMemberDetails(intialiseMemberDetails());
    setAccessTokenData(initialAccessToken);
    setUserDetails(initialUserDetails);
    setLoaderDetails(initialLoaderDetails);
    setBillDetails(initialBillDetails);
    setOneTimePaymentErrors(initialOneTimePaymentErrors);
    setScheduledEmailErrors(initialScheduledEmailErrors);
    setWalletsData(initialWalletsData);
    setWalletAlertDetails(initialWalletAlertData);
    setBinderPayNowLoginDetails(initialBinderPayNowLoginDetails);
    setBinderSchedulePaymentResponse(initialSchedulePaymentResponse);
    setBinderImmediatePaymentResponse(initialBinderImmediatePayment);
    setBinderBillDetails(initialBinderBillDetails);
    setBinderPaymentDetails(initialBinderPaymentDetails);
    setAutopayEnrolment(initialHPSEnrolment);
  };
  const [APIStatus, dispatchAPIStatus] = useReducer(
    apiStatusReducer,
    initialAPIStatus,
  );
  const { getUserSpecificHeader } = useAPIHeaders();
  const { userType, isActiveUser, isOneTimePaymentActiveUser, isCA } =
    useUserType();

  const setAPIStatus = (statusType: string, action: string) => {
    dispatchAPIStatus({
      name: statusType,
      type: action,
    });
  };

  const resetWalletAlertDetails = () => {
    setWalletAlertDetails(initialWalletAlertData);
  };

  const resetAutopayAlertDetails = () => {
    setAutopayAlertDetails(initialAutopayAlertData);
  };

  const [PKCEState, dispatchPKCEStatus] = useReducer(
    pkceReducer,
    initialPKCEStatus,
    pkceReducerInit,
  );

  const setPKCEStatus = (statusType: string, action: string) => {
    dispatchPKCEStatus({
      name: statusType,
      type: action,
    });
  };

  const setAuthenticationError = (
    error: IAuthenticationError,
    callBack?: () => void,
  ) => {
    const { medicareURL } = getStateCodeSpecificUrlUsingProcessEnv();
    if (error && error.showError) {
      if (!error.authenticationError) {
        let errorMessage = error.errAPIResponse?.errorMessage;
        const { stateCode, dataSource, derivedLOB, govtMember } =
          error.errAPIResponse ?? {};
        if (errorMessage) {
          errorMessage = errorMessage
            ?.replace(
              HELPDESK_NUMBER_PLACEHOLDER,
              getStateCodeCustomerServiceNumberByUserType(stateCode),
            )
            .replace(
              CUSTOMER_SERVICE_NUMBER_PLACEHOLDER_CAMELCASE,
              govtMember
                ? getCustomerServiceNumberForGovernment({
                    dataSourceParam: dataSource,
                    derivedLobParam: derivedLOB,
                    stateParam: stateCode as MEMBER_STATE_CODE,
                  })
                : getStateCodeCustomerServiceNumberByUserType(
                    error.errAPIResponse?.stateCode,
                  ),
            )
            .replace(
              MEDICARE_LINK,
              `<a href=${medicareURL}>${MEDICARE_LINK_TEXT}</a>`,
            );
        }
        if (stateCode && govtMember && !isLoaded) {
          fetchFromContentService(
            COMMON_COMPONENTS.HOURS_OF_OPERATION,
            language,
            stateCode,
            false,
          )
            .then((res) => {
              const hoursOfOperation = getHoursOfOperationMessageForGov({
                dataSourceParam: dataSource,
                derivedLobParam: derivedLOB,
                errorContent: res?.response,
                isAuthenticatedError: true,
              });

              setMemberAuthenticationError({
                showError: true,
                authenticationError: false,
                errorMessage,
                errAPIResponse: error?.errAPIResponse,
                hoursOfOperation,
              });
            })
            .catch((err) => {
              if (err && !err.redirectingToErrorScreen) {
                setMemberAuthenticationError({
                  showError: true,
                  authenticationError: false,
                  errorMessage,
                  errAPIResponse: error?.errAPIResponse,
                });
              }
            });
        }
        setMemberAuthenticationError({
          showError: true,
          authenticationError: false,
          errorMessage,
          errAPIResponse: error?.errAPIResponse,
        });
      } else {
        setMemberAuthenticationError({
          showError: true,
          authenticationError: true,
          errorMessage: '',
        });
      }
    }
    if (callBack) {
      callBack();
    }
  };

  const getFiservResponse = (
    fundingToken: string | undefined,
    fundingAccountLastFourDigits: string | undefined,
  ) => {
    if (fundingToken && fundingAccountLastFourDigits) {
      return {
        fundingAccountValidationResult: {
          fundingAccount: {
            fundingAccountToken: fundingToken,
            fundingAccountLastFourDigit: fundingAccountLastFourDigits,
          },
        },
      };
    }
    return undefined;
  };

  const getGovernmentSelectedBillDetails = (
    productDetail: IGovernmentProductDetails,
    memberBillingInfoRequestPayload: IMemberBillingInfoRequest,
    sessionMid: number,
    origin?: string,
    policyId?: string,
    retailOrGovMemberResponse?: any,
  ) => {
    if (productDetail) {
      const {
        derivedLob,
        dataSource,
        state,
        subcriberId,
        mppId,
        planBenefitPackageId,
        cmsContractNumber,
      } = productDetail;
      const { dateOfBirth, mid, zipCode } =
        memberBillingInfoRequestPayload ?? {};

      setMemberDetails({
        ...memberDetails,
        userType: getGovtUserType(origin, false),
        memberId: subcriberId,
        mppId: mppId,
        memberBillingInfoRequestPayload: {
          ...memberBillingInfoRequestPayload,
          dateOfBirth: dateOfBirth || '',
          mid: mid || sessionMid,
          zipCode: zipCode || '',
          derivedLob,
          dataSource,
          state,
          productDetail,
          planBenefitPackageId,
          cmsContractNumber,
        },
        policyId,
        retailOrGovMemberResponse,
      });
      setPaymentDetails({
        ...paymentDetails,
        selectedBillCimNumber: subcriberId,
      });
    }
  };

  const hydrateStore = (billsResponseData: IBillResponseAndSessionData) => {
    const {
      billsResponse,
      sessionData,
      billsInfo,
      allProductDetails,
      memberBillingInfoRequestPayload,
    } = billsResponseData ?? {};
    const { ORIGIN, POLICY_ID, RETAIL_GOVT_DETAILS } = getSessionData();

    if (billsResponse.billDetails.length > 1 && billsInfo) {
      const {
        noBillIndicator,
        billInfoCim,
        billInfoCaseNumber,
        billInfoExchangeIndicator,
        redirectToAllPayments,
      } = billsInfo;
      setBillDetails({
        ...billDetails,
        // save bills in sorted order
        bills: getSortedBillsAscByDueDate(billsResponse.billDetails),
        hasMultipleBills: true,
        noBillIndicator,
        hasMultipleBillsWithValue: redirectToAllPayments,
        displayAutopayDraftDate: getDisplayAutopayDraftDate(
          billsResponse.billDetails,
        ),
        noBillFound: billsResponse?.noBillFound,
        billsLoaded: true,
      });
      const selectedBill =
        allProductDetails &&
        getSelectedCimDetails(
          sessionData?.SELECTED_CIM,
          billsResponse.billDetails,
          Number(sessionData?.MPP_ID),
        );
      const productDetail =
        allProductDetails &&
        selectedBill &&
        getProductDetail(allProductDetails, selectedBill);
      productDetail &&
        memberBillingInfoRequestPayload &&
        getGovernmentSelectedBillDetails(
          productDetail,
          memberBillingInfoRequestPayload,
          Number(sessionData?.M_ID),
          ORIGIN,
        );
      if (sessionData && sessionData.MEMBER_ID && sessionData.M_ID) {
        const {
          SELECTED_CIM,
          PAYMENT_AMOUNT,
          PAYMENT_TYPE,
          PAYMENT_DATE,
          SELECTED_CASE,
          PAYMENT_FUNDING_ACCOUNT_LAST_FOUR_DIGIT,
          PAYMENT_FUNDING_ACCOUNT_TOKEN,
          MEMBER_PAYMENT_METHOD,
          MEMBER_SUBSCRIBER_DETAILS,
          PAYMENT_METHOD,
          PAYMENT_CONFIRMATION_NUMBER,
          PAYMENT_DATE_TIMESTAMP,
          SELECTED_PAYMENT_METHOD,
          SELECTED_ADD_WALLET_TYPE,
          SELECTED_WALLET_FUNDING_ACCOUNT_TOKEN,
          SELECTED_WALLET_FUNDING_LAST_FOUR_DIGIT,
          SELECTED_SCHEDULED_TRANSACTION,
          WALLET_FUNDING_ACCOUNT_TOKEN,
          PAYMENT_API,
          ADD_TO_WALLET,
          SELECTED_RECENT_PAYMENT_TRACKER,
        } = sessionData;
        setPaymentDetails({
          ...paymentDetails,
          selectedBillCimNumber: SELECTED_CIM,
          selectedCaseNumber: SELECTED_CASE,
          paymentAmount: PAYMENT_AMOUNT ? Number(PAYMENT_AMOUNT) : undefined,
          paymentType: PAYMENT_TYPE,
          memberExchangeIndicator: getExchangeIndicatorFromMemberBills(
            billsResponse.billDetails ||
              JSON.parse(MEMBER_SUBSCRIBER_DETAILS as string)?.subcriberDetails,
          ),
          selectedBillExchangeIndicator:
            SELECTED_CIM &&
            !JSON.parse(MEMBER_SUBSCRIBER_DETAILS as string).govtMember
              ? JSON.parse(
                  MEMBER_SUBSCRIBER_DETAILS as string,
                )?.subcriberDetails.find(
                  (bill: any) => bill.subcriberCimNumber === SELECTED_CIM,
                ).exchangeIndicator
              : paymentDetails.selectedBillExchangeIndicator || '',
          paymentDate: PAYMENT_DATE
            ? getDateInUIFormat(PAYMENT_DATE)
            : undefined,
          addToWallet: ADD_TO_WALLET ? Boolean(ADD_TO_WALLET) : false,
          paymentMethod: PAYMENT_METHOD,
          fundingAccountToken: WALLET_FUNDING_ACCOUNT_TOKEN,
          memberPaymentMethod: MEMBER_PAYMENT_METHOD,
          fiservResponse: getFiservResponse(
            PAYMENT_FUNDING_ACCOUNT_TOKEN,
            PAYMENT_FUNDING_ACCOUNT_LAST_FOUR_DIGIT,
          ),
          selectedPaymentMethodType: SELECTED_PAYMENT_METHOD,
        });
        setMenuItemsConfiguration(
          JSON.parse(sessionData.MENU_ITEMS_CONFIGURATION as string),
        );
        setPaymentApiType(PAYMENT_API as PAYMENT_API_TYPE);
        if (
          PAYMENT_API === PAYMENT_API_TYPE.SCHEDULED ||
          PAYMENT_API === PAYMENT_API_TYPE.UPDATED
        ) {
          setScheduleOneTimePaymentDetails({
            ...scheduleOneTimePaymentDetails,
            referenceCode: PAYMENT_CONFIRMATION_NUMBER,
            paymentAmount: Number(PAYMENT_AMOUNT),
          });
        }
        setImmediatePayDetails({
          ...immediatePayDetails,
          confirmationCode: PAYMENT_CONFIRMATION_NUMBER,
          paymentDateTimeStamp: PAYMENT_DATE_TIMESTAMP,
        });
        if (SELECTED_SCHEDULED_TRANSACTION) {
          const selectedTransaction: IScheduledTransaction = JSON.parse(
            SELECTED_SCHEDULED_TRANSACTION,
          );
          setSelectedScheduledTransaction({
            ...selectedScheduledTransaction,
            ...selectedTransaction,
          });
        }
        if (SELECTED_ADD_WALLET_TYPE) {
          setWalletsData({
            ...walletsData,
            selectedAddWalletType:
              SELECTED_ADD_WALLET_TYPE as PAYMENT_METHOD_TYPES,
          });
        }
        if (
          SELECTED_WALLET_FUNDING_ACCOUNT_TOKEN &&
          SELECTED_WALLET_FUNDING_LAST_FOUR_DIGIT
        ) {
          setWalletsData({
            ...walletsData,
            selectedWallet: {
              fundingAccountLastFourDigit:
                SELECTED_WALLET_FUNDING_LAST_FOUR_DIGIT,
              fundingAccountToken: SELECTED_WALLET_FUNDING_ACCOUNT_TOKEN,
            },
          });
        }
        if (SELECTED_RECENT_PAYMENT_TRACKER) {
          setSelectedRecentPayment({
            ...selectedRecentPayment,
            ...JSON.parse(SELECTED_RECENT_PAYMENT_TRACKER),
          });
        }
      } else {
        // When user logs in for the first time / non-refresh scenario

        //changes are required for government member
        setPaymentDetails({
          memberExchangeIndicator: getExchangeIndicatorFromMemberBills(
            billsResponse.billDetails,
          ),
          selectedBillCimNumber: billInfoCim || '',
          selectedCaseNumber: billInfoCaseNumber || '',
          selectedBillExchangeIndicator:
            billInfoExchangeIndicator ||
            paymentDetails.selectedBillExchangeIndicator ||
            billsResponse.billDetails[0].exchangeIndicator,
        });
      }
    } else {
      if (billsInfo) {
        const { noBillIndicator, redirectToAllPayments } = billsInfo;
        setBillDetails({
          ...billDetails,
          bills: billsResponse.billDetails,
          hasMultipleBills: false,
          noBillIndicator,
          hasMultipleBillsWithValue: redirectToAllPayments,
          displayAutopayDraftDate:
            billsResponse.billDetails[0]?.autoPayDraftDate || '',
          noBillFound: billsResponse?.noBillFound,
          billsLoaded: true,
        });
        const productDetail =
          allProductDetails &&
          getProductDetail(
            allProductDetails,
            billsResponse.billDetails[0],
            true,
          );
        productDetail &&
          memberBillingInfoRequestPayload &&
          getGovernmentSelectedBillDetails(
            productDetail,
            memberBillingInfoRequestPayload,
            Number(sessionData?.M_ID),
            ORIGIN,
            POLICY_ID,
            RETAIL_GOVT_DETAILS && JSON.parse(RETAIL_GOVT_DETAILS as string),
          );
      }

      if (sessionData && sessionData.MEMBER_ID && sessionData.M_ID) {
        const {
          PAYMENT_AMOUNT,
          PAYMENT_TYPE,
          PAYMENT_DATE,
          PAYMENT_FUNDING_ACCOUNT_LAST_FOUR_DIGIT,
          PAYMENT_FUNDING_ACCOUNT_TOKEN,
          MEMBER_PAYMENT_METHOD,
          MEMBER_SUBSCRIBER_DETAILS,
          PAYMENT_METHOD,
          PAYMENT_CONFIRMATION_NUMBER,
          PAYMENT_DATE_TIMESTAMP,
          ADD_TO_WALLET,
          SELECTED_PAYMENT_METHOD,
          SELECTED_ADD_WALLET_TYPE,
          SELECTED_WALLET_FUNDING_LAST_FOUR_DIGIT,
          SELECTED_WALLET_FUNDING_ACCOUNT_TOKEN,
          SELECTED_SCHEDULED_TRANSACTION,
          WALLET_FUNDING_ACCOUNT_TOKEN,
          PAYMENT_API,
          SELECTED_RECENT_PAYMENT_TRACKER,
        } = sessionData;
        setMenuItemsConfiguration(
          JSON.parse(sessionData.MENU_ITEMS_CONFIGURATION as string),
        );
        setPaymentDetails({
          selectedBillCimNumber: billsResponse.billDetails[0].cim || '',
          selectedCaseNumber: billsResponse.billDetails[0].caseNumber || '',
          paymentAmount: PAYMENT_AMOUNT ? Number(PAYMENT_AMOUNT) : undefined,
          paymentType: PAYMENT_TYPE,
          paymentDate: PAYMENT_DATE
            ? getDateInUIFormat(PAYMENT_DATE)
            : undefined,
          selectedBillExchangeIndicator:
            billsResponse.billDetails[0].exchangeIndicator ||
            (!JSON.parse(MEMBER_SUBSCRIBER_DETAILS as string).govtMember &&
              JSON.parse(MEMBER_SUBSCRIBER_DETAILS as string)
                ?.subcriberDetails[0].exchangeIndicator) ||
            '',
          memberExchangeIndicator: getExchangeIndicatorFromMemberBills(
            billsResponse.billDetails ||
              JSON.parse(MEMBER_SUBSCRIBER_DETAILS as string),
          ),
          addToWallet: ADD_TO_WALLET ? Boolean(ADD_TO_WALLET) : false,
          memberPaymentMethod: MEMBER_PAYMENT_METHOD,
          paymentMethod: PAYMENT_METHOD,
          fundingAccountToken: WALLET_FUNDING_ACCOUNT_TOKEN,
          fiservResponse: getFiservResponse(
            PAYMENT_FUNDING_ACCOUNT_TOKEN,
            PAYMENT_FUNDING_ACCOUNT_LAST_FOUR_DIGIT,
          ),
          selectedPaymentMethodType: SELECTED_PAYMENT_METHOD,
        });
        setPaymentApiType(PAYMENT_API as PAYMENT_API_TYPE);
        if (
          PAYMENT_API === PAYMENT_API_TYPE.SCHEDULED ||
          PAYMENT_API === PAYMENT_API_TYPE.UPDATED
        ) {
          setScheduleOneTimePaymentDetails({
            ...scheduleOneTimePaymentDetails,
            referenceCode: PAYMENT_CONFIRMATION_NUMBER,
            paymentAmount: Number(PAYMENT_AMOUNT),
          });
        }
        setImmediatePayDetails({
          ...immediatePayDetails,
          confirmationCode: PAYMENT_CONFIRMATION_NUMBER,
          paymentDateTimeStamp: PAYMENT_DATE_TIMESTAMP,
        });
        if (SELECTED_SCHEDULED_TRANSACTION) {
          const selectedTransaction: IScheduledTransaction = JSON.parse(
            SELECTED_SCHEDULED_TRANSACTION,
          );
          setSelectedScheduledTransaction({
            ...selectedScheduledTransaction,
            ...selectedTransaction,
          });
        }
        if (SELECTED_ADD_WALLET_TYPE) {
          setWalletsData({
            ...walletsData,
            selectedAddWalletType:
              SELECTED_ADD_WALLET_TYPE as PAYMENT_METHOD_TYPES,
          });
        }
        if (
          SELECTED_WALLET_FUNDING_ACCOUNT_TOKEN &&
          SELECTED_WALLET_FUNDING_LAST_FOUR_DIGIT
        ) {
          setWalletsData({
            ...walletsData,
            selectedWallet: {
              fundingAccountLastFourDigit:
                SELECTED_WALLET_FUNDING_LAST_FOUR_DIGIT,
              fundingAccountToken: SELECTED_WALLET_FUNDING_ACCOUNT_TOKEN,
            },
          });
        }
        if (SELECTED_RECENT_PAYMENT_TRACKER) {
          setSelectedRecentPayment({
            ...selectedRecentPayment,
            ...JSON.parse(SELECTED_RECENT_PAYMENT_TRACKER),
          });
        }
      } else {
        // When user logs in for the first time / non-refresh scenario
        const { USER_TYPE, ORIGIN } = getSessionData() ?? {};
        const { noBillFound } = billsResponse ?? {};
        setPaymentDetails({
          selectedBillCimNumber:
            isGovernmentUser({
              userTypeFromSession: USER_TYPE ?? '',
              originFromSession: ORIGIN ?? '',
            }) && noBillFound
              ? billsResponse.billDetails[0]?.subcriberId // setting subcriberId for selectedBillCimNumber for no bill scenario , used later to get respective bill details
              : billsResponse.billDetails[0]?.cim || '',
          selectedBillExchangeIndicator:
            billsResponse.billDetails[0]?.exchangeIndicator || '',
          memberExchangeIndicator: !isGovernmentUser({
            userTypeFromSession: USER_TYPE ?? '',
            originFromSession: ORIGIN ?? '',
          })
            ? getExchangeIndicatorFromMemberBills(billsResponse.billDetails)
            : { exchangeIndicator: '', cim: '', caseNumber: '' },
          selectedCaseNumber: billsResponse.billDetails[0]?.caseNumber || '',
          addToWallet: false,
        });
      }
    }
    setIsHydrating(false);
  };

  useEffect(() => {
    retrieveStateCodeFromUrl();
  }, []);

  useEffect(() => {
    // Set page title based on brand and state code
    // HealthSpring has no state code, so brandCode alone is sufficient
    setPageTitle(stateCode, brandCode);
  }, [brandCode, stateCode]);

  useEffect(() => {
    const sessionData = getSessionData();
    const route = window.location.pathname;

    if (brandCode === BRAND_CODES.HEALTHSPRING) {
      setIsHydrating(false);
      return;
    }

    const canProceedWithHydration = Boolean(stateCode);

    if (canProceedWithHydration) {
      if (sessionData && sessionData.SELECTED_TAB) {
        setSelectedTabUrl(sessionData.SELECTED_TAB);
      }
      if (sessionData.USER_TYPE && sessionData.ORIGIN) {
        setMemberDetails({
          ...memberDetails,
          userType: Number(sessionData?.USER_TYPE),
        });
        setUserDetails({
          ...userDetails,
          origin: getOriginType(sessionData.ORIGIN),
        });
      }
      const isActiveGovernment =
        (userType === USER_TYPES.BAM_GOVERNMENT_ACTIVE_MEMBER &&
          sessionData.ORIGIN === ORIGIN_TYPES.BAM_GOVERNMENT_ACTIVE) ||
        (userType === USER_TYPES.ONE_TIME_GOVERNMENT_ACTIVE_MEMBER &&
          sessionData.ORIGIN === ORIGIN_TYPES.ONE_TIME_GOVERNMENT_ACTIVE) ||
        (userType === USER_TYPES.NMA_GOVT_USER &&
          (sessionData.ORIGIN === ORIGIN_TYPES.NMA ||
            sessionData.ORIGIN === ORIGIN_TYPES.UMA)) ||
        userType === USER_TYPES.CA_GOVT_USER;
      const sessionDataCheck =
        sessionData &&
        sessionData.ORIGIN &&
        routeMap[sessionData.ORIGIN as string].includes(route) &&
        (route !== ROUTES.guest.memberValidation ||
          route !== ROUTES.active.memberValidation);
      if (
        (sessionDataCheck && sessionData.MEMBER_ID && !isActiveGovernment) ||
        (isActiveGovernment && sessionDataCheck)
      ) {
        if (
          (isActiveUser || isOneTimePaymentActiveUser || isActiveGovernment) &&
          sessionData?.ACCESS_TOKEN &&
          sessionData?.ACCESS_TOKEN !== null &&
          sessionData?.USER_TYPE &&
          sessionData?.USER_TYPE !== null &&
          sessionData?.ORIGIN &&
          sessionData?.ORIGIN !== null
        ) {
          const { emailId, ...otherMemberSubcriberDetails } = JSON.parse(
            sessionData.MEMBER_SUBSCRIBER_DETAILS as string,
          );
          setAccessTokenData({
            ...accessTokenData,
            access_token: sessionData?.ACCESS_TOKEN,
            id_token: sessionData?.ID_TOKEN as string,
            expires_at: sessionData?.ACCESS_TOKEN_EXPIRY_TIME,
            refresh_token: sessionData?.REFRESH_TOKEN as string,
          });
          setMemberDetails({
            ...memberDetails,
            memberId: sessionData.MEMBER_ID,
            mppId: Number(sessionData.MPP_ID),
            userType: Number(sessionData?.USER_TYPE), // converting to enum
            memberBillingInfoRequestPayload: {
              ...otherMemberSubcriberDetails,
              mid: Number(sessionData.M_ID),
            },
            conversionTypeDetails: sessionData.CONVERSION_TYPE_DETAILS && {
              ...JSON.parse(sessionData.CONVERSION_TYPE_DETAILS as string),
            },
            policyId: sessionData.POLICY_ID,
            retailOrGovMemberResponse: sessionData.RETAIL_GOVT_DETAILS && {
              ...JSON.parse(sessionData.RETAIL_GOVT_DETAILS as string),
            },
          });
          setPlanSelected(sessionData.PLAN_SELECTED as PLAN_TYPES);
          setUserDetails({
            ...userDetails,
            origin: getOriginType(sessionData.ORIGIN),
            emailId,
          });
          if (sessionData.READ_ONLY_VIEW !== undefined) {
            setReadOnlyView(Boolean(JSON.parse(sessionData.READ_ONLY_VIEW)));
          }
          if (sessionData.LIMIT_FUNCTIONALITY !== undefined) {
            setLimitFunctionality(
              Boolean(JSON.parse(sessionData.LIMIT_FUNCTIONALITY)),
            );
          }
        } else {
          if (
            sessionData.MEMBER_SUBSCRIBER_DETAILS !== undefined &&
            sessionData.MEMBER_SUBSCRIBER_DETAILS !== null
          ) {
            const { emailId, brokerDetails, ...otherMemberSubcriberDetails } =
              JSON.parse(sessionData.MEMBER_SUBSCRIBER_DETAILS as string);
            if (
              areValuesDefined(sessionData, [
                'BROKER_AUTOPAY_PAYMENT_METHOD',
                'EMAIL_CONFIRMATION_STATUS',
                'BROKER_FUNDING_ACCOUNT_DETAILS',
              ])
            ) {
              setBrokerAutopayPaymentMethod(
                sessionData.BROKER_AUTOPAY_PAYMENT_METHOD as string,
              );
              setEmailConfirmationStatus(
                JSON.parse(sessionData.EMAIL_CONFIRMATION_STATUS as string),
              );
              setFundingAccountDetailsForBroker(
                JSON.parse(
                  sessionData.BROKER_FUNDING_ACCOUNT_DETAILS as string,
                ),
              );
            }
            setReferenceCodeForBroker(referenceCodeForBroker as string);
            setFormatedDate(formatedDate as string);
            setMemberDetails({
              ...memberDetails,
              memberId: sessionData.MEMBER_ID,
              mppId: Number(sessionData.MPP_ID),
              userType: Number(sessionData?.USER_TYPE), // converting to enum
              memberBillingInfoRequestPayload: {
                ...otherMemberSubcriberDetails,
                mid: Number(sessionData.M_ID),
              },
              brokerDetails: brokerDetails,
            });
            setUserDetails({
              ...userDetails,
              origin: getOriginType(sessionData.ORIGIN),
              emailId,
            });
          } else {
            setMemberDetails({
              ...memberDetails,
              memberId: sessionData.MEMBER_ID,
              mppId: Number(sessionData.MPP_ID),
              userType: Number(sessionData?.USER_TYPE), // converting to enum
              memberBillingInfoRequestPayload: {
                subcriberDetails: [],
                mid: Number(sessionData.M_ID),
                dateOfBirth: '',
                zipCode: '',
                state: stateCode,
              },
              policyId: sessionData.POLICY_ID,
            });
            setUserDetails({
              ...userDetails,
              origin: getOriginType(sessionData.ORIGIN),
              emailId: '',
            });
          }
        }
        if (sessionData?.M_ID) {
          setTimeout(() => {
            API.maintenance.getScheduledMaintenance(
              (endDateTime, message) => {
                setScheduledMaintenanceDetails({
                  endDateTime,
                  message,
                  active: true,
                  isLoaded: true,
                });
                history.push(ROUTES.guest.allPaymentDetails);
              },
              () => {
                if (userType) {
                  let headers = getUserSpecificHeader({
                    MEMBER_ID: sessionData.MEMBER_ID + '',
                    MPP_ID: Number(sessionData.MPP_ID + ''),
                    STATE_CODE: stateCode,
                  });
                  if (userType === USER_TYPES.BROKER) {
                    const {
                      brokerDetails: { brokerId },
                    } = JSON.parse(
                      sessionData.MEMBER_SUBSCRIBER_DETAILS as string,
                    );
                    headers = {
                      ...headers,
                      USER_TYPES: userType,
                      Broker_id: brokerId,
                    };
                  }
                  const storeBillDetailsByUser = ({
                    bills,
                  }: IOnSuccessPaymentBills) => {
                    //TODO - update code for guest government during handling noBillIndicator scenario
                    const userTypeFromSession = sessionData?.USER_TYPE ?? '';

                    const originFromSession = sessionData?.ORIGIN ?? '';
                    const checkIsGovernmentUser = isGovernmentUser({
                      userTypeFromSession,
                      originFromSession,
                    });
                    const parsedMemberSubscriberDetails = JSON.parse(
                      sessionData.MEMBER_SUBSCRIBER_DETAILS as string,
                    );
                    const { billDetails, noBillFound } = bills;
                    const billsInfo = checkIsGovernmentUser
                      ? getGovtBillResponseInfo(
                          billDetails,
                          parsedMemberSubscriberDetails?.derivedLob,
                          noBillFound || false,
                        )
                      : getBillsResponseInfo(bills.billDetails);
                    const { noBillIndicator } = billsInfo;
                    const updatedBills = noBillIndicator
                      ? updateAmountAndMinAmountForNoBills(
                          bills,
                          checkIsGovernmentUser,
                        )
                      : bills;
                    hydrateStore({
                      billsResponse: updatedBills,
                      sessionData,
                      billsInfo,
                      allProductDetails:
                        parsedMemberSubscriberDetails.allProductDetails,
                      memberBillingInfoRequestPayload:
                        parsedMemberSubscriberDetails,
                    });
                  };

                  // eslint-disable-next-line
                  const { emailId, ...otherMemberSubcriberDetails } =
                    JSON.parse(sessionData.MEMBER_SUBSCRIBER_DETAILS as string);
                  const userTypeFromSession = sessionData?.USER_TYPE ?? '';
                  const originFromSession = sessionData?.ORIGIN ?? '';
                  const checkIsGovernmentUser = isGovernmentUser({
                    userTypeFromSession,
                    originFromSession,
                  });
                  const {
                    dateOfBirth,
                    zipCode,
                    productDetail,
                    allProductDetails,
                  } =
                    JSON.parse(
                      sessionData.MEMBER_SUBSCRIBER_DETAILS as string,
                    ) ?? {};
                  const memberBillingInfoRequestPayloadData =
                    checkIsGovernmentUser
                      ? {
                          dateOfBirth,
                          zipCode,
                          productDetail: isActiveGovernment
                            ? allProductDetails
                            : productDetail,
                          mid: Number(sessionData.M_ID),
                          policyId: sessionData.POLICY_ID,
                        }
                      : {
                          ...otherMemberSubcriberDetails,
                          mid: Number(sessionData.M_ID),
                          policyId: sessionData.POLICY_ID,
                        };

                  if (checkIsGovernmentUser) {
                    const parsedMemberSubscriberDetails = JSON.parse(
                      sessionData.MEMBER_SUBSCRIBER_DETAILS as string,
                    );
                    let govtHeaders = {
                      ...headers,
                    };

                    if (isActiveGovernment) {
                      setHasGovtScheduledPayments(
                        Boolean(
                          sessionData.HAS_GOVT_SCHEDULE_PAYMENTS_INDICATOR,
                        ),
                      );
                      setSchedulePaymentCount(
                        Number(sessionData.SCHEDULE_PAYMENT_COUNT),
                      );
                    }
                    if (
                      userType.toString() ===
                        USER_TYPES.ONE_TIME_GOVERNMENT_ACTIVE_MEMBER.toString() ||
                      userType.toString() ===
                        USER_TYPES.BAM_GOVERNMENT_ACTIVE_MEMBER.toString() ||
                      userType.toString() ===
                        USER_TYPES.NMA_GOVT_USER.toString()
                    ) {
                      govtHeaders = {
                        ...headers,
                        AUTHORIZATION: `Bearer ${
                          sessionData.ACCESS_TOKEN !== '' &&
                          sessionData.ACCESS_TOKEN
                        }`,
                        USER_TYPE_CODE: userType,
                      };
                    } else {
                      govtHeaders = {
                        ...headers,
                        DATA_SOURCE: parsedMemberSubscriberDetails?.dataSource,
                        LOB: parsedMemberSubscriberDetails?.derivedLob,
                        USER_TYPE_CODE: Number(sessionData?.USER_TYPE),
                        PRODUCT_TYPE:
                          parsedMemberSubscriberDetails?.productDetail
                            ?.productType,
                      };
                    }
                    const isAllPaymentDetailsPage =
                      window.location.pathname ===
                      ROUTES.active.allPaymentDetails;
                    const getEmailAddressForActiveGovMember = () => {
                      API.member
                        .getEmailForActiveUser({
                          ...govtHeaders,
                          MID: sessionData.M_ID,
                        })
                        .then((emailResponse) => {
                          if (emailResponse.response) {
                            setMemberEmail(emailResponse.response);
                          }
                        })
                        .catch(() => {
                          setMemberEmail({ email: null });
                        });
                    };
                    API.billing
                      .getGovernmentMemberPaymentBills(
                        Number(sessionData?.USER_TYPE),
                        govtHeaders,
                        memberBillingInfoRequestPayloadData,
                      )
                      .then((bills) => {
                        storeBillDetailsByUser({
                          bills,
                        });
                        !isAllPaymentDetailsPage &&
                          getEmailAddressForActiveGovMember();
                      })
                      .catch(() => setIsHydrating(false));
                  } else {
                    API.billing
                      .getPaymentBills(
                        userType,
                        headers,
                        memberBillingInfoRequestPayloadData,
                      )
                      .then((bills) => {
                        storeBillDetailsByUser({ bills });
                        const headers: IActiveMemberEmailHeaders = {
                          MEMBER_ID: sessionData.MEMBER_ID || '',
                          MPP_ID: Number(sessionData.MPP_ID),
                          STATE_CODE: stateCode,
                          MID: sessionData.M_ID || '',
                        };
                        if (isActiveUser || isCA) {
                          API.member
                            .getEmailForActiveUser(
                              getUserSpecificHeader(headers),
                            )
                            .then((emailResponse) => {
                              if (emailResponse.response) {
                                setMemberEmail(emailResponse.response);
                              }
                            })
                            .catch(() => {
                              setMemberEmail({ email: null });
                            });
                        } else {
                          const { brokerDetails } = JSON.parse(
                            sessionData.MEMBER_SUBSCRIBER_DETAILS as string,
                          );
                          const brokerhHeaders: IActiveMemberEmailHeaders = {
                            ...headers,
                            BROKER_ID: brokerDetails.brokerId,
                            USER_TYPE_CODE: userType,
                          };
                          API.member
                            .getEmailForBroker(brokerhHeaders)
                            .then((emailResponse) => {
                              if (emailResponse.response) {
                                setMemberEmail(emailResponse.response);
                              }
                            })
                            .catch(() => {
                              setMemberEmail({ email: null });
                            });
                        }
                      })
                      .catch(() => setIsHydrating(false));
                  }
                }
              },
              () => {
                setIsHydrating(false);
              },
              SCHEDULE_MAINTENANCE_APPLICATION_TYPES['MEMBER'],
            );
          }, 0);
        } else {
          setIsHydrating(false);
        }
      } // TILL HERE
      else if (
        sessionData &&
        sessionData?.BINDER_PAY_NOW_MEMBER_DETAILS &&
        JSON.parse(sessionData?.BINDER_PAY_NOW_MEMBER_DETAILS).applicationId
      ) {
        setBinderPayNowLoginDetails({
          ...binderPayNowLoginDetails,
          ...JSON.parse(sessionData?.BINDER_PAY_NOW_MEMBER_DETAILS),
        });
        if (sessionData?.BINDER_PAYMENT_ACCOUNT) {
          setBinderPaymentMethod({
            ...binderPaymentMethod,
            ...JSON.parse(sessionData?.BINDER_PAYMENT_ACCOUNT || ''),
          });
        }
        if (sessionData?.AUTOPAY_BINDER_PAYMENT_ACCOUNT) {
          setAutopayBinderPaymentMethod({
            ...autopayBinderPaymentMethod,
            ...JSON.parse(sessionData?.AUTOPAY_BINDER_PAYMENT_ACCOUNT || ''),
          });
        }
        if (sessionData && sessionData.PAY_NOW_CONFIRMATION_NUMBER) {
          setBinderPayNowTransactionId(
            sessionData?.PAY_NOW_CONFIRMATION_NUMBER,
          );
        }
        if (sessionData && sessionData.ORIGIN) {
          setUserDetails({
            ...userDetails,
            origin: getOriginType(sessionData?.ORIGIN),
          });
        }
        setIsHydrating(false);
      } else if (sessionData?.BINDER_PAYLATER_LOGIN_DETAILS) {
        setApplicantBinderDetails({
          ...applicantBinderDetails,
          ...JSON.parse(sessionData?.BINDER_PAYLATER_LOGIN_DETAILS),
        });
        if (sessionData.SELECTED_SCHEDULED_TRANSACTION) {
          setSelectedScheduledTransaction({
            ...selectedScheduledTransaction,
            ...JSON.parse(sessionData.SELECTED_SCHEDULED_TRANSACTION),
          });
        }
        if (sessionData.SCHEDULEPAYMENTS_LIST) {
          setListScheduledOneTimePayments({
            ...listScheduledOneTimePayments,
            ...JSON.parse(sessionData.SCHEDULEPAYMENTS_LIST),
          });
        }
        if (sessionData.BINDER_PAYMENT_API_TYPE) {
          setBinderPaymentApiType(
            sessionData.BINDER_PAYMENT_API_TYPE as PAYMENT_API_TYPE,
          );
        }
        if (sessionData?.BINDER_PAYMENT_ACCOUNT) {
          setBinderPaymentMethod({
            ...binderPaymentMethod,
            ...JSON.parse(sessionData?.BINDER_PAYMENT_ACCOUNT || ''),
          });
        }
        if (sessionData?.AUTOPAY_BINDER_PAYMENT_ACCOUNT) {
          setAutopayBinderPaymentMethod({
            ...autopayBinderPaymentMethod,
            ...JSON.parse(sessionData?.AUTOPAY_BINDER_PAYMENT_ACCOUNT || ''),
          });
        }
        if (sessionData?.BINDER_IMMEDIATE_PAYMENT_RESPONSE) {
          setBinderImmediatePaymentResponse({
            ...binderImmediatePaymentResponse,
            ...JSON.parse(sessionData?.BINDER_IMMEDIATE_PAYMENT_RESPONSE),
          });
        }
        if (sessionData?.BINDER_SCHEDULE_PAYMENT_RESPONSE) {
          setBinderSchedulePaymentResponse({
            ...binderSchedulePaymentResponse,
            ...JSON.parse(sessionData?.BINDER_SCHEDULE_PAYMENT_RESPONSE),
          });
        }
        if (sessionData?.AUTOPAYMENT_DETAILS) {
          setAutoPaymentDetails({
            ...autoPaymentDetails,
            ...JSON.parse(sessionData.AUTOPAYMENT_DETAILS),
          });
        }
        if (sessionData?.BINDER_PAYMENT_DETAILS) {
          setBinderPaymentDetails({
            ...binderPaymentDetails,
            ...JSON.parse(sessionData?.BINDER_PAYMENT_DETAILS || ''),
          });
        }
        if (sessionData?.CAID) {
          setCaId(sessionData?.CAID);
        }
        setIsHydrating(false);
      } else {
        setIsHydrating(false);
      }
    }
  }, [stateCode]);

  const getConfirmationCode = () => {
    if (immediatePayDetails.confirmationCode) {
      return immediatePayDetails.confirmationCode;
    } else if (scheduleOneTimePaymentDetails.referenceCode) {
      return scheduleOneTimePaymentDetails.referenceCode;
    } else {
      return undefined;
    }
  };

  const getSelectedScheduledTransactionDetails = () => {
    if (selectedScheduledTransaction.referenceId.length === 0) {
      return undefined;
    }
    const selectedTransaction: IScheduledTransaction = {
      cim: selectedScheduledTransaction.cim,
      referenceId: selectedScheduledTransaction.referenceId,
      referenceCode: selectedScheduledTransaction.referenceCode,
      paymentDate: selectedScheduledTransaction.paymentDate,
      paymentAmount: selectedScheduledTransaction.paymentAmount,
      fundingAccount: {
        fundingAccountLastFourDigit:
          selectedScheduledTransaction.fundingAccount
            .fundingAccountLastFourDigit,
        fundingAccountToken:
          selectedScheduledTransaction.fundingAccount.fundingAccountToken,
        fundingCategory:
          selectedScheduledTransaction.fundingAccount.fundingCategory,
        nickname: selectedScheduledTransaction.fundingAccount.nickname,
        bankAccountType:
          selectedScheduledTransaction.fundingAccount.bankAccountType,
      },
    };
    return JSON.stringify(selectedTransaction);
  };

  /**
   * added this check , since cannot use from userUsertype hook
   * there is race condition in updating context and useUserType
   */
  const isBinderPayNowUser = () => {
    return (
      memberDetails.userType === USER_TYPES.BINDER_MEMBER &&
      userDetails.origin === ORIGIN_TYPES.BINDER_PAY_NOW
    );
  };

  const isBinderPayLaterUser = () => {
    return (
      memberDetails.userType === USER_TYPES.BINDER_MEMBER &&
      userDetails.origin === ORIGIN_TYPES.BINDER_PAY_LATER
    );
  };

  const isCaBinderPayLaterUser = () => {
    return (
      memberDetails.userType === USER_TYPES.CUSTOMER_ADVOCATE &&
      userDetails.origin === ORIGIN_TYPES.CA_BINDER_PAY_LATER
    );
  };

  const isGovernmentUser = ({
    userTypeFromSession,
    originFromSession,
  }: {
    userTypeFromSession: string;
    originFromSession: string;
  }) => {
    return (
      (Number(userTypeFromSession) === USER_TYPES.GOVERNMENT_GUEST &&
        getOriginType(originFromSession) === ORIGIN_TYPES.GUEST_GOVERNMENT) ||
      (Number(userTypeFromSession) ===
        USER_TYPES.ONE_TIME_GOVERNMENT_ACTIVE_MEMBER &&
        getOriginType(originFromSession) ===
          ORIGIN_TYPES.ONE_TIME_GOVERNMENT_ACTIVE) ||
      (Number(userTypeFromSession) ===
        USER_TYPES.BAM_GOVERNMENT_ACTIVE_MEMBER &&
        getOriginType(originFromSession) ===
          ORIGIN_TYPES.BAM_GOVERNMENT_ACTIVE) ||
      (Number(userTypeFromSession) === USER_TYPES.NMA_GOVT_USER &&
        (getOriginType(originFromSession) === ORIGIN_TYPES.NMA ||
          getOriginType(originFromSession) === ORIGIN_TYPES.UMA)) ||
      Number(userTypeFromSession) === USER_TYPES.CA_GOVT_USER
    );
  };

  useEffect(() => {
    const isActiveGovernmentOrCAGovernment =
      memberDetails &&
      (memberDetails.userType ===
        USER_TYPES.ONE_TIME_GOVERNMENT_ACTIVE_MEMBER ||
        memberDetails.userType === USER_TYPES.BAM_GOVERNMENT_ACTIVE_MEMBER ||
        memberDetails.userType === USER_TYPES.CA_GOVT_USER ||
        memberDetails.userType === USER_TYPES.NMA_GOVT_USER);
    const IFM = 'IFM';

    if (
      (!isActiveGovernmentOrCAGovernment &&
        memberDetails.memberId &&
        memberDetails.mppId &&
        memberDetails.memberBillingInfoRequestPayload &&
        memberDetails.memberBillingInfoRequestPayload.mid) ||
      (isActiveGovernmentOrCAGovernment &&
        memberDetails.memberBillingInfoRequestPayload &&
        memberDetails.memberBillingInfoRequestPayload.mid)
    ) {
      const {
        ACCESS_TOKEN,
        REFRESH_TOKEN,
        USER_TYPE,
        USER_NAME,
        ACCESS_TOKEN_EXPIRY_TIME,
        MEMBER_ID,
        M_ID,
        SELECTED_CIM,
        PAYMENT_AMOUNT,
        PAYMENT_TYPE,
        MPP_ID,
        SELECTED_CASE,
        SELECTED_PAYMENT_METHOD,
        PAYMENT_FUNDING_ACCOUNT_LAST_FOUR_DIGIT,
        PAYMENT_FUNDING_ACCOUNT_TOKEN,
        PAYMENT_CONFIRMATION_NUMBER,
        PAYMENT_DATE_TIMESTAMP,
        MEMBER_PAYMENT_METHOD,
        PAYMENT_METHOD,
        SELECTED_WALLET_FUNDING_ACCOUNT_TOKEN,
        SELECTED_WALLET_FUNDING_LAST_FOUR_DIGIT,
        SELECTED_ADD_WALLET_TYPE,
        SELECTED_SCHEDULED_TRANSACTION,
        PAYMENT_API,
        PAYMENT_DATE,
        ADD_TO_WALLET,
        WALLET_FUNDING_ACCOUNT_TOKEN,
        ORIGIN,
        ID_TOKEN,
        SELECTED_TAB,
        MEMBER_SUBSCRIBER_DETAILS,
        READ_ONLY_VIEW,
        LIMIT_FUNCTIONALITY,
        CONVERSION_TYPE_DETAILS,
        SELECTED_RECENT_PAYMENT_TRACKER,
        LOB,
        BROKER_AUTOPAY_PAYMENT_METHOD,
        MENU_ITEMS_CONFIGURATION,
        POLICY_ID,
        PLAN_SELECTED,
        RETAIL_GOVT_DETAILS,
        HAS_GOVT_SCHEDULE_PAYMENTS_INDICATOR,
        SCHEDULE_PAYMENT_COUNT,
        BROKER_FUNDING_ACCOUNT_DETAILS,
        EMAIL_CONFIRMATION_STATUS,
        BROKER_REFERENCE_CODE,
        FORMATED_DATE,
      } = SESSION_DATA;
      const productDetails =
          memberDetails?.memberBillingInfoRequestPayload?.productDetail,
        productType = productDetails?.productType,
        originTypeLOB =
          userDetails.origin !== null &&
          userDetails.origin &&
          userDetails.origin, // used for checking govt user or not before storing LOB in session storage
        userTypeLOB =
          memberDetails.userType !== null
            ? memberDetails.userType
            : userType !== null && userType, // used for checking govt user or not before storing LOB in session storage
        isGovtUserLOB = isGovernmentUser({
          userTypeFromSession: userTypeLOB ? userTypeLOB.toString() : '',
          originFromSession: originTypeLOB ? originTypeLOB.toString() : '',
        });

      const updateSessionDataGuest = {
        memberId: {
          sessionStorageKey: MEMBER_ID,
          value: memberDetails.memberId,
        },
        mid: {
          sessionStorageKey: M_ID,
          value: memberDetails.memberBillingInfoRequestPayload.mid,
        },
        mppId: { sessionStorageKey: MPP_ID, value: memberDetails.mppId },
        memberSubscriberDetails: {
          sessionStorageKey: MEMBER_SUBSCRIBER_DETAILS,
          value: {
            subcriberDetails:
              memberDetails?.memberBillingInfoRequestPayload?.subcriberDetails,
            dateOfBirth:
              memberDetails?.memberBillingInfoRequestPayload?.dateOfBirth,
            zipCode: memberDetails?.memberBillingInfoRequestPayload?.zipCode,
            state: memberDetails?.memberBillingInfoRequestPayload?.state,
            govtMember:
              memberDetails?.memberBillingInfoRequestPayload?.govtMember,
            productDetail:
              memberDetails?.memberBillingInfoRequestPayload?.productDetail,
            emailId: userDetails?.emailId,
            planName: memberDetails?.memberBillingInfoRequestPayload.planName,
            cmsContractNumber:
              memberDetails?.memberBillingInfoRequestPayload.cmsContractNumber,
            planBenefitPackageId:
              memberDetails?.memberBillingInfoRequestPayload
                ?.planBenefitPackageId,
            dataSource:
              memberDetails?.memberBillingInfoRequestPayload?.dataSource,
            derivedLob:
              memberDetails?.memberBillingInfoRequestPayload?.derivedLob,
            allProductDetails:
              memberDetails?.memberBillingInfoRequestPayload?.allProductDetails,
            termedMember:
              memberDetails?.memberBillingInfoRequestPayload?.termedMember,
            brokerDetails: memberDetails?.brokerDetails,
          },
        },
        selectedCIM: {
          sessionStorageKey: SELECTED_CIM,
          value: paymentDetails.selectedBillCimNumber,
        },
        paymentAmount: {
          sessionStorageKey: PAYMENT_AMOUNT,
          value: paymentDetails.paymentAmount,
        },
        paymentType: {
          sessionStorageKey: PAYMENT_TYPE,
          value: paymentDetails.paymentType,
        },
        selectedCase: {
          sessionStorageKey: SELECTED_CASE,
          value: paymentDetails.selectedCaseNumber,
        },
        paymentFundingAccountLastFourDigit: {
          sessionStorageKey: PAYMENT_FUNDING_ACCOUNT_LAST_FOUR_DIGIT,
          value:
            paymentDetails.fiservResponse?.fundingAccountValidationResult
              ?.fundingAccount?.fundingAccountLastFourDigit,
        },
        paymentFundingToken: {
          sessionStorageKey: PAYMENT_FUNDING_ACCOUNT_TOKEN,
          value:
            paymentDetails.fiservResponse?.fundingAccountValidationResult
              ?.fundingAccount?.fundingAccountToken,
        },
        memberPaymentMethod: {
          sessionStorageKey: MEMBER_PAYMENT_METHOD,
          value: paymentDetails.memberPaymentMethod,
        },
        paymentMethod: {
          sessionStorageKey: PAYMENT_METHOD,
          value: paymentDetails.paymentMethod,
        },
        paymentConfirmationNumber: {
          sessionStorageKey: PAYMENT_CONFIRMATION_NUMBER,
          value: getConfirmationCode(),
        },
        paymentDateTimestamp: {
          sessionStorageKey: PAYMENT_DATE_TIMESTAMP,
          value: immediatePayDetails.paymentDateTimeStamp,
        },
        selectedPaymentMethod: {
          sessionStorageKey: SELECTED_PAYMENT_METHOD,
          value: paymentDetails.selectedPaymentMethodType, //TODO verify it , it was earlier paymentMethod in MMA
        },
        paymentDate: {
          sessionStorageKey: PAYMENT_DATE,
          value: paymentDetails.paymentDate,
        },
        user_type: {
          sessionStorageKey: USER_TYPE,
          value:
            memberDetails.userType !== null
              ? memberDetails.userType
              : userType !== null && userType,
        },
        origin: {
          sessionStorageKey: ORIGIN,
          value:
            userDetails.origin !== null &&
            userDetails.origin &&
            userDetails.origin,
        },
        paymentApi: {
          sessionStorageKey: PAYMENT_API,
          value: paymentApiType,
        },
        walletFundingAccountToken: {
          sessionStorageKey: SELECTED_WALLET_FUNDING_ACCOUNT_TOKEN,
          value: walletsData.selectedWallet?.fundingAccountToken,
        },
        walletFundingAccountLastFourDigit: {
          sessionStorageKey: SELECTED_WALLET_FUNDING_LAST_FOUR_DIGIT,
          value: walletsData.selectedWallet?.fundingAccountLastFourDigit,
        },
        lob: {
          sessionStorageKey: LOB,
          value: isGovtUserLOB
            ? productType && `Government_${productType}`
            : IFM,
        },
        brokerAutopayPaymentMethod: {
          sessionStorageKey: BROKER_AUTOPAY_PAYMENT_METHOD,
          value: brokerAutopayPaymentMethod,
        },
        fundingAccountDetailsForBroker: {
          sessionStorageKey: BROKER_FUNDING_ACCOUNT_DETAILS,
          value: fundingAccountDetailsForBroker,
        },
        referenceCodeForBroker: {
          sessionStorageKey: BROKER_REFERENCE_CODE,
          value: referenceCodeForBroker,
        },
        formatedDate: {
          sessionStorageKey: FORMATED_DATE,
          value: formatedDate,
        },
        emailConfirmationStatus: {
          sessionStorageKey: EMAIL_CONFIRMATION_STATUS,
          value: emailConfirmationStatus,
        },
        menuItemsConfiguration: {
          sessionStorageKey: MENU_ITEMS_CONFIGURATION,
          value: menuItemsConfiguration,
        },
        policyId: {
          sessionStorageKey: POLICY_ID,
          value: memberDetails.policyId,
        },
      };
      if (isActiveUser || isActiveGovernmentOrCAGovernment) {
        const updateSessionDataActive = {
          ...updateSessionDataGuest,
          selectedScheduledTransactionReferenceId: {
            sessionStorageKey: SELECTED_SCHEDULED_TRANSACTION,
            value: getSelectedScheduledTransactionDetails(),
          },
          walletToken: {
            sessionStorageKey: WALLET_FUNDING_ACCOUNT_TOKEN,
            value: paymentDetails.fundingAccountToken,
          },
          paymentDate: {
            sessionStorageKey: PAYMENT_DATE,
            value: paymentDetails.paymentDate,
          },
          addToWallet: {
            sessionStorageKey: ADD_TO_WALLET,
            value: paymentDetails.addToWallet,
          },
          selectedAddWalletType: {
            sessionStorageKey: SELECTED_ADD_WALLET_TYPE,
            value: walletsData.selectedAddWalletType,
          },
          access_token: {
            sessionStorageKey: ACCESS_TOKEN,
            value:
              accessTokenData.access_token !== null &&
              accessTokenData.access_token,
          },
          readOnlyView: {
            sessionStorageKey: READ_ONLY_VIEW,
            value: readOnlyView,
          },
          limitFunctionality: {
            sessionStorageKey: LIMIT_FUNCTIONALITY,
            value: limitFunctionality,
          },
          id_token: {
            sessionStorageKey: ID_TOKEN,
            value:
              accessTokenData.id_token !== null && accessTokenData.id_token,
          },
          user_name: {
            sessionStorageKey: USER_NAME,
            value: userDetails.userName !== null && userDetails.userName,
          },
          expiry_time: {
            sessionStorageKey: ACCESS_TOKEN_EXPIRY_TIME,
            value: accessTokenData.expires_at,
          },
          refresh_token: {
            sessionStorageKey: REFRESH_TOKEN,
            value:
              accessTokenData.refresh_token !== null &&
              accessTokenData.refresh_token,
          },
          origin: {
            sessionStorageKey: ORIGIN,
            value:
              userDetails.origin !== null &&
              userDetails.origin &&
              userDetails.origin,
          },
          selectedTabUrl: {
            sessionStorageKey: SELECTED_TAB,
            value: selectedTabUrl,
          },
          conversionTypeDetails: {
            sessionStorageKey: CONVERSION_TYPE_DETAILS,
            value: memberDetails?.conversionTypeDetails,
          },
          selectedRecentPayment: {
            sessionStorageKey: SELECTED_RECENT_PAYMENT_TRACKER,
            value: selectedRecentPayment,
          },
          planSelected: {
            sessionStorageKey: PLAN_SELECTED,
            value: planSelected,
          },
          retailGovtDetails: {
            sessionStorageKey: RETAIL_GOVT_DETAILS,
            value: memberDetails?.retailOrGovMemberResponse,
          },
          hasGovtScheduledPayments: {
            sessionStorageKey: HAS_GOVT_SCHEDULE_PAYMENTS_INDICATOR,
            value: hasGovtScheduledPayments,
          },
          schedulePaymentCount: {
            sessionStorageKey: SCHEDULE_PAYMENT_COUNT,
            value: schedulePaymentCount,
          },
        };
        updateSessionData(updateSessionDataActive);
      } else {
        updateSessionData(updateSessionDataGuest);
      }
    }
    if (binderPayNowLoginDetails.applicationId) {
      const {
        BINDER_PAY_NOW_MEMBER_DETAILS,
        ORIGIN,
        USER_TYPE,
        BINDER_PAYMENT_ACCOUNT,
        AUTOPAY_BINDER_PAYMENT_ACCOUNT,
        PAY_NOW_CONFIRMATION_NUMBER,
        LOB,
      } = SESSION_DATA;
      if (isBinderPayNowUser()) {
        const updateSessionDataForBinderPayNow = {
          binderPayNowMemberDetails: {
            sessionStorageKey: BINDER_PAY_NOW_MEMBER_DETAILS,
            value: binderPayNowLoginDetails,
          },
          origin: {
            sessionStorageKey: ORIGIN,
            value: userDetails.origin !== null && userDetails.origin,
          },
          user_type: {
            sessionStorageKey: USER_TYPE,
            value:
              memberDetails.userType !== null
                ? memberDetails.userType
                : userType !== null && userType,
          },
          binderPaymentMethod: {
            sessionStorageKey: BINDER_PAYMENT_ACCOUNT,
            value: binderPaymentMethod,
          },
          paymentFundingToken: {
            sessionStorageKey: AUTOPAY_BINDER_PAYMENT_ACCOUNT,
            value: autopayBinderPaymentMethod,
          },
          binderPayNowTransactionId: {
            sessionStorageKey: PAY_NOW_CONFIRMATION_NUMBER,
            value: binderPayNowTransactionId,
          },
          lob: {
            sessionStorageKey: LOB,
            value: IFM,
          },
        };
        updateSessionData(updateSessionDataForBinderPayNow);
      }
    }
    if (isBinderPayLaterUser() || isCaBinderPayLaterUser()) {
      const {
        AUTOPAY_BINDER_PAYMENT_ACCOUNT,
        BINDER_PAYMENT_ACCOUNT,
        BINDER_PAYLATER_LOGIN_DETAILS,
        BINDER_IMMEDIATE_PAYMENT_RESPONSE,
        BINDER_SCHEDULE_PAYMENT_RESPONSE,
        BINDER_PAYMENT_DETAILS,
        SELECTED_SCHEDULED_TRANSACTION,
        BINDER_PAYMENT_API_TYPE,
        AUTOPAYMENT_DETAILS,
        ORIGIN,
        USER_TYPE,
        SCHEDULEPAYMENTS_LIST,
        CAID,
        LOB,
        MENU_ITEMS_CONFIGURATION,
      } = SESSION_DATA;

      const updateSessionDataForBinderPayLater = {
        binderPaymentMethod: {
          sessionStorageKey: BINDER_PAYMENT_ACCOUNT,
          value: binderPaymentMethod,
        },
        selectedScheduledTransactionReferenceId: {
          sessionStorageKey: SELECTED_SCHEDULED_TRANSACTION,
          value: getSelectedScheduledTransactionDetails(),
        },
        listScheduledpayments: {
          sessionStorageKey: SCHEDULEPAYMENTS_LIST,
          value: listScheduledOneTimePayments,
        },
        binderPaymentApiType: {
          sessionStorageKey: BINDER_PAYMENT_API_TYPE,
          value: binderPaymentApiType,
        },
        applicantBinderDetails: {
          sessionStorageKey: BINDER_PAYLATER_LOGIN_DETAILS,
          value: applicantBinderDetails,
        },
        autopayBinderPaymentMethod: {
          sessionStorageKey: AUTOPAY_BINDER_PAYMENT_ACCOUNT,
          value: autopayBinderPaymentMethod,
        },
        origin: {
          sessionStorageKey: ORIGIN,
          value: userDetails.origin !== null && userDetails.origin,
        },
        user_type: {
          sessionStorageKey: USER_TYPE,
          value:
            memberDetails.userType !== null
              ? memberDetails.userType
              : userType !== null && userType,
        },
        binderSchedulePaymentResponse: {
          sessionStorageKey: BINDER_SCHEDULE_PAYMENT_RESPONSE,
          value: binderSchedulePaymentResponse,
        },
        binderImmediatePaymentResponse: {
          sessionStorageKey: BINDER_IMMEDIATE_PAYMENT_RESPONSE,
          value: binderImmediatePaymentResponse,
        },
        binderPaymentDetails: {
          sessionStorageKey: BINDER_PAYMENT_DETAILS,
          value: binderPaymentDetails,
        },
        autoPaymentDetails: {
          sessionStorageKey: AUTOPAYMENT_DETAILS,
          value: autoPaymentDetails,
        },
        caId: {
          sessionStorageKey: CAID,
          value: caId,
        },
        lob: {
          sessionStorageKey: LOB,
          value: IFM,
        },
        menuItemsConfiguration: {
          sessionStorageKey: MENU_ITEMS_CONFIGURATION,
          value: menuItemsConfiguration,
        },
      };
      updateSessionData(updateSessionDataForBinderPayLater);
    }
  }, [
    memberDetails,
    paymentDetails,
    billDetails,
    immediatePayDetails,
    scheduleOneTimePaymentDetails,
    selectedScheduledTransaction,
    walletsData,
    paymentApiType,
    memberEmail,
    accessTokenData,
    userType,
    userDetails,
    binderPayNowLoginDetails,
    autopayBinderPaymentMethod,
    binderPaymentMethod,
    selectedTabUrl,
    binderImmediatePaymentResponse,
    binderSchedulePaymentResponse,
    autoPaymentDetails,
    brokerAutopayPaymentMethod,
    binderPaymentApiType,
    binderPayNowTransactionId,
    readOnlyView,
    limitFunctionality,
    caId,
    selectedRecentPayment,
    menuItemsConfiguration,
    planSelected,
    hasGovtScheduledPayments,
    fundingAccountDetailsForBroker,
    formatedDate,
  ]);

  const retrieveStateCodeFromUrl = () => {
    const code = getStateCodeFromParsedUrl();
    setStateCode(code);
  };

  const fetchWallets = ({ headers, onSuccess, onError }: IFetchWallet) => {
    const { mppId, memberId } = memberDetails;
    const updateSchedulePaymentWalletData =
      paymentApiType === PAYMENT_API_TYPE.UPDATED
        ? selectedScheduledTransaction.fundingAccount
        : null;
    if (mppId && memberId && stateCode) {
      API.wallet
        .getWallets(
          {
            ...headers,
            MPP_ID: mppId,
            MEMBER_ID: memberId,
            STATE_CODE: stateCode,
          },
          authDetails,
          userType as number,
          updateSchedulePaymentWalletData,
        )
        .then((res: IGetWalletResponse) => {
          if (res && res.response && res.response.walletFundingAccounts) {
            const { walletFundingAccounts } = res.response;
            setWalletsData({
              ...walletsData, //this is done so that we can retain both selectedWallet and wallets in context (selectedWallet value would be removed once fetchWallets api is called again)
              wallets: addTypeToWalletsList(walletFundingAccounts),
            });
          }
          if (onSuccess) {
            onSuccess();
          }
        })
        .catch(() => {
          setWalletsData({
            wallets: [],
          });
          if (onError) {
            onError();
          }
        });
    }
  };

  const fetchBinderWallets = ({
    headers,
    onSuccess,
    onError,
  }: IFetchWallet) => {
    const updateSchedulePaymentWalletData =
      binderPaymentApiType === PAYMENT_API_TYPE.UPDATED
        ? selectedScheduledTransaction.fundingAccount
        : null;
    if (headers && stateCode) {
      API.wallet
        .getBinderWallets(headers, authDetails, updateSchedulePaymentWalletData)
        .then((res: IGetWalletResponse) => {
          if (res && res.response && res.response.walletFundingAccounts) {
            const { walletFundingAccounts } = res.response;
            setWalletsData({
              ...walletsData, //this is done so that we can retain both selectedWallet and wallets in context (selectedWallet value would be removed once fetchWallets api is called again)
              wallets: addTypeToWalletsList(walletFundingAccounts),
            });
          }
          if (onSuccess) {
            onSuccess();
          }
        })
        .catch(() => {
          setWalletsData({
            wallets: [],
          });
          if (onError) {
            onError();
          }
        });
    }
  };

  return (
    <AppContext.Provider
      value={useMemo(
        () => ({
          paymentDetails,
          setPaymentDetails,
          resetContext,
          memberDetails,
          setMemberDetails,
          userDetails,
          setUserDetails,
          accessTokenData,
          setAccessTokenData,
          memberAuthenticationError, // For the login page alert box
          setMemberAuthenticationError,
          setAuthenticationError,
          billDetails,
          setBillDetails,
          loaderDetails,
          showLoader,
          hideLoader,
          hydrateStore,
          isHydrating,
          retrieveStateCodeFromUrl,
          stateCode,
          authDetails,
          setAuthDetails,
          immediatePayDetails,
          setImmediatePayDetails,
          scheduleOneTimePaymentDetails,
          setScheduleOneTimePaymentDetails,
          listScheduledOneTimePayments,
          setListScheduledOneTimePayments,
          selectedScheduledTransaction,
          setSelectedScheduledTransaction,
          autoPaymentDetails,
          setAutoPaymentDetails,
          setAPIStatus,
          APIStatus,
          PKCEState,
          setPKCEStatus,
          setPaymentMethodOptions,
          paymentMethodOptions,
          setScheduledMaintenanceDetails,
          scheduledMaintenanceDetails,
          language,
          setScheduledEmailErrors,
          setOneTimePaymentErrors,
          oneTimePaymentErrors,
          scheduledEmailErrors,
          walletsData,
          setWalletsData,
          paymentApiType,
          setPaymentApiType,
          fetchWallets,
          fetchBinderWallets,
          walletAlertDetails,
          setWalletAlertDetails,
          isBrokerUserWithAutopayFailed,
          setIsBrokerUserWithAutopayFailed,
          globalTechnicalDifficulty,
          setGlobalTechnicalDifficulty,
          resetWalletAlertDetails,
          setAutoPayPaymentMethods,
          autoPayPaymentMethods,
          binderPaymentMethod,
          setBinderPaymentMethod,
          autopayBinderPaymentMethod,
          setAutopayBinderPaymentMethod,
          binderPaymentDetails,
          setBinderPaymentDetails,
          autopayAlertDetails,
          setAutopayAlertDetails,
          resetAutopayAlertDetails,
          binderPayNowLoginDetails,
          setBinderPayNowLoginDetails,
          binderBillDetails,
          setBinderBillDetails,
          applicantBinderDetails,
          binderSchedulePaymentResponse,
          setBinderSchedulePaymentResponse,
          binderImmediatePaymentResponse,
          setBinderImmediatePaymentResponse,
          setApplicantBinderDetails,
          setSelectedTabUrl,
          selectedTabUrl,
          binderPaymentApiType,
          setBinderPaymentApiType,
          displayAutopayDetails,
          setDisplayAutopayDetails,
          binderPayNowTransactionId,
          setBinderPayNowTransactionId,
          setShowConversionModal,
          showConversionModal,
          showAutopaySetupModal,
          setShowAutopaySetupModal,
          isLoadingAfterLogin,
          setIsLoadingAfterLogin,
          readOnlyView,
          setReadOnlyView,
          limitFunctionality,
          setLimitFunctionality,
          setOldConversionModal,
          showOldConversionModal,
          memberEmail,
          setMemberEmail,
          setCaId,
          caId,
          autopayEnrolment,
          setAutopayEnrolment,
          selectedRecentPayment,
          setSelectedRecentPayment,
          menuItemsConfiguration,
          setMenuItemsConfiguration,
          planSelected,
          setPlanSelected,
          showChangeViewPlanOptions,
          setChangeViewPlanOptions,
          hasGovtScheduledPayments,
          setHasGovtScheduledPayments,
          schedulePaymentCount,
          setSchedulePaymentCount,
          isMedsupAutopayLoaded,
          setIsMedsupAutopayLoaded,
          brokerAutopayPaymentMethod,
          setBrokerAutopayPaymentMethod,
          emailConfirmationStatus,
          setEmailConfirmationStatus,
          fundingAccountDetailsForBroker,
          setFundingAccountDetailsForBroker,
          referenceCodeForBroker,
          setReferenceCodeForBroker,
          formatedDate,
          setFormatedDate,
          errorCodeForValidateBroker,
          setErrorCodeForValidateBroker,
          showEconsentModal,
          setShowEconsentModal,
        }),
        [
          paymentDetails,
          memberDetails,
          memberAuthenticationError,
          billDetails,
          loaderDetails,
          isHydrating,
          stateCode,
          authDetails,
          accessTokenData,
          userDetails,
          immediatePayDetails,
          scheduleOneTimePaymentDetails,
          listScheduledOneTimePayments,
          selectedScheduledTransaction,
          autoPaymentDetails,
          APIStatus,
          PKCEState,
          paymentMethodOptions,
          scheduledMaintenanceDetails,
          oneTimePaymentErrors,
          scheduledEmailErrors,
          globalTechnicalDifficulty,
          walletsData,
          paymentApiType,
          walletAlertDetails,
          autoPayPaymentMethods,
          binderPaymentMethod,
          autopayBinderPaymentMethod,
          autopayAlertDetails,
          binderPayNowLoginDetails,
          binderPaymentDetails,
          binderSchedulePaymentResponse,
          binderImmediatePaymentResponse,
          binderBillDetails,
          applicantBinderDetails,
          selectedTabUrl,
          binderPaymentApiType,
          displayAutopayDetails,
          binderPayNowTransactionId,
          showConversionModal,
          readOnlyView,
          limitFunctionality,
          showOldConversionModal,
          memberEmail,
          caId,
          autopayEnrolment,
          selectedRecentPayment,
          showAutopaySetupModal,
          showEconsentModal,
          menuItemsConfiguration,
          isLoadingAfterLogin,
          planSelected,
          showChangeViewPlanOptions,
          hasGovtScheduledPayments,
          schedulePaymentCount,
          isMedsupAutopayLoaded,
          brokerAutopayPaymentMethod,
          emailConfirmationStatus,
          fundingAccountDetailsForBroker,
          referenceCodeForBroker,
          formatedDate,
          errorCodeForValidateBroker,
        ],
      )}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useAppContext(): IAppContextValue {
  return useContext(AppContext);
}
